import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { InsuranceFlowStateHistoryStatus } from 'src/app/_enums/insurance-flow/insurance-flow-history-status.enum';
import { InsuranceFlowStatus } from 'src/app/_enums/insurance-flow/insurance-flow-status.enum';
import { InsuranceFlowStateHistory } from 'src/app/_types/insurance-flow.types';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { StepperComponent } from 'src/app/shared/ui-kit/stepper/stepper.component';
import {
  UiStepperState,
  UiStepperStep,
} from 'src/app/shared/ui-kit/stepper/stepper.types';

import { TaskDetailsService } from '../../task-details.service';
import { CENTRALISATION_STEP_CONFIG } from './centralisation-history.constants';
import { CancelCentralisationComponent } from './stepper-actions/cancel-centralisation/cancel-centralisation.component';
import { CreateCentralisationPolicyComponent } from './stepper-actions/create-centralisation-policy/create-centralisation-policy.component';
import { SendCentralisationComponent } from './stepper-actions/send-centralisation/send-centralisation.component';

const CENTRALISATION_STATUSES = new Set<InsuranceFlowStateHistoryStatus>([
  InsuranceFlowStatus.PENDING_CENTRALISATION,
  InsuranceFlowStatus.ONGOING_CENTRALISATION,
  InsuranceFlowStatus.FINISHED_CENTRALISATION,
  InsuranceFlowStatus.CANCELLED_CENTRALISATION,
]);

@Component({
  selector: 'app-centralisation-history',
  standalone: true,
  imports: [CommonModule, StepperComponent, SpinnerComponent],
  templateUrl: './centralisation-history.component.html',
  styleUrl: './centralisation-history.component.css',
})
export class CentralisationHistoryComponent implements OnInit, OnDestroy {
  protected flowSteps: UiStepperStep[] = [];

  protected isLoading = false;

  protected hasCentralisationData = false;

  private stateSubscription?: Subscription;

  constructor(private readonly taskDetailsService: TaskDetailsService) {}

  ngOnInit(): void {
    this.stateSubscription = this.taskDetailsService.state$.subscribe(
      (state) => {
        this.isLoading = state.isLoading;
        const statusHistory = state.insuranceFlowData?.statusHistory ?? [];
        const currentStatus = state.insuranceFlowData?.status;

        this.hasCentralisationData =
          CENTRALISATION_STATUSES.has(currentStatus) ||
          statusHistory.some((entry) =>
            CENTRALISATION_STATUSES.has(entry.status)
          );

        this.flowSteps =
          this.hasCentralisationData && currentStatus
            ? this.buildFlowSteps(currentStatus, statusHistory)
            : [];
      }
    );
  }

  ngOnDestroy(): void {
    this.stateSubscription?.unsubscribe();
  }

  private getStepActionConfig(
    status: InsuranceFlowStatus
  ): Pick<UiStepperStep, 'action' | 'actionComponent'> {
    switch (status) {
      case InsuranceFlowStatus.PENDING_CENTRALISATION:
        return { actionComponent: { component: SendCentralisationComponent } };
      case InsuranceFlowStatus.ONGOING_CENTRALISATION:
        return {
          actionComponent: { component: CreateCentralisationPolicyComponent },
        };
      case InsuranceFlowStatus.CANCELLED_CENTRALISATION:
        return {
          actionComponent: { component: CancelCentralisationComponent },
        };
      default:
        return {};
    }
  }

  private buildFlowSteps(
    currentStatus: InsuranceFlowStatus,
    statusHistory: InsuranceFlowStateHistory[]
  ): UiStepperStep[] {
    const historyStatuses = new Set(statusHistory.map((entry) => entry.status));
    const currentStepIndex = CENTRALISATION_STEP_CONFIG.findIndex(
      (step) => step.status === currentStatus
    );

    return CENTRALISATION_STEP_CONFIG.map((step, stepIndex) => {
      const isActive = step.status === currentStatus;
      const isDone = historyStatuses.has(step.status) && !isActive;
      const statusEntry = this.findLatestStatusEntry(
        statusHistory,
        step.status
      );
      const shouldShowAction =
        isActive ||
        step.status === InsuranceFlowStatus.CANCELLED_CENTRALISATION;
      const { action = undefined, actionComponent = undefined } =
        shouldShowAction ? this.getStepActionConfig(step.status) : {};

      return {
        id: step.id,
        label: step.label,
        required: step.required,
        subtitle: this.buildHistoryCaption(statusEntry),
        state: this.resolveStepState(
          isActive,
          isDone,
          currentStatus,
          stepIndex,
          currentStepIndex
        ),
        action,
        actionComponent,
      };
    });
  }

  private resolveStepState(
    isActive: boolean,
    isDone: boolean,
    currentStatus: InsuranceFlowStatus,
    stepIndex: number,
    currentStepIndex: number
  ): UiStepperState {
    const shouldRenderAsError =
      currentStatus === InsuranceFlowStatus.CANCELLED_CENTRALISATION &&
      (isActive || isDone);
    if (shouldRenderAsError) {
      return 'error';
    }

    if (isActive) {
      return 'active';
    }

    if (isDone) {
      return 'completed';
    }

    if (
      currentStepIndex !== -1 &&
      stepIndex < currentStepIndex &&
      currentStatus !== InsuranceFlowStatus.CANCELLED_CENTRALISATION
    ) {
      return 'completed';
    }

    return 'pending';
  }

  private findLatestStatusEntry(
    statusHistory: InsuranceFlowStateHistory[],
    status: InsuranceFlowStatus
  ): InsuranceFlowStateHistory | undefined {
    return statusHistory
      .filter((entry) => entry.status === status)
      .sort(
        (a, b) =>
          new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime()
      )[0];
  }

  private buildHistoryCaption(
    entry?: InsuranceFlowStateHistory
  ): string | undefined {
    if (!entry) {
      return undefined;
    }

    const date = this.formatHistoryDate(entry.endedOn);
    const fullName = entry.endedByFullName?.trim();

    if (fullName && date) {
      return `${fullName} ${date}`;
    }

    return fullName || date || undefined;
  }

  private formatHistoryDate(updatedOn: string): string {
    const date = new Date(updatedOn);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }).format(date);
  }
}
